import React, { useEffect, useState } from "react";
import { usePermissions } from "../../PermissionsProvider";
import { TagService } from "../../services/TagService";
import { Button } from "../ui/button";
import { Card, CardContent } from "../ui/card";
import { Badge } from "../ui/badge";
import {
  PlusIcon,
  PencilIcon,
  Trash2Icon,
  PlayIcon,
  Loader2Icon,
  TagIcon,
  TextIcon,
  BrainCircuitIcon,
  FilterIcon,
  LayersIcon,
  ArrowRightIcon,
} from "lucide-react";
import TagDefinitionModal, { TagDefinition } from "./TagDefinitionModal";
import TagRunDrawer from "./TagRunDrawer";
import { toast } from "sonner";

const RULE_TYPE_ICONS: Record<string, React.ReactNode> = {
  phrase_match: <TextIcon className="w-4 h-4" />,
  regex: <span className="font-mono text-xs font-bold">.*</span>,
  metadata: <FilterIcon className="w-4 h-4" />,
  llm_classifier: <BrainCircuitIcon className="w-4 h-4" />,
  composite: <LayersIcon className="w-4 h-4" />,
  sequence: <ArrowRightIcon className="w-4 h-4" />,
};

const RULE_TYPE_LABELS: Record<string, string> = {
  phrase_match: "Phrase",
  regex: "Regex",
  metadata: "Metadata",
  llm_classifier: "LLM",
  composite: "Composite",
  sequence: "Sequence",
};

const SCOPE_LABELS: Record<string, string> = {
  request: "Request",
  session: "Session",
};

const TagsPage: React.FC = () => {
  const { apiKey } = usePermissions();
  const [tags, setTags] = useState<TagDefinition[]>([]);
  const [loading, setLoading] = useState(false);
  const [modalOpen, setModalOpen] = useState(false);
  const [editingTag, setEditingTag] = useState<TagDefinition | null>(null);
  const [deleting, setDeleting] = useState<number | null>(null);
  const [runningTag, setRunningTag] = useState<TagDefinition | null>(null);
  const [runDrawerOpen, setRunDrawerOpen] = useState(false);

  const loadTags = () => {
    return TagService.list(setLoading, apiKey).subscribe(
      (data: TagDefinition[]) => setTags(data),
      (err: any) => console.error("Failed to load tags", err)
    );
  };

  useEffect(() => {
    const sub = loadTags();
    return () => sub.unsubscribe();
  }, [apiKey]);

  const handleNew = () => {
    setEditingTag(null);
    setModalOpen(true);
  };

  const handleEdit = (t: TagDefinition) => {
    setEditingTag(t);
    setModalOpen(true);
  };

  const handleDelete = (t: TagDefinition) => {
    if (!t.id) return;
    setDeleting(t.id);
    TagService.deleteTag(t.id, () => {}, apiKey).subscribe(
      () => {
        setTags((prev) => prev.filter((x) => x.id !== t.id));
        toast.success(`Deleted "${t.name}"`);
        setDeleting(null);
      },
      (err: any) => {
        console.error("Failed to delete tag", err);
        toast.error("Failed to delete tag");
        setDeleting(null);
      }
    );
  };

  const handleRun = (t: TagDefinition) => {
    setRunningTag(t);
    setRunDrawerOpen(true);
  };

  const handleModalSave = (saved: TagDefinition) => {
    if (editingTag) {
      setTags((prev) => prev.map((x) => (x.id === saved.id ? saved : x)));
    } else {
      setTags((prev) => [...prev, saved]);
    }
    setModalOpen(false);
    setEditingTag(null);
  };

  const baseTags = tags.filter((t) => t.is_base);
  const derivedTags = tags.filter((t) => !t.is_base);

  return (
    <div className="space-y-6">
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-gc-demi-bold text-foreground">
            Data Tags
          </h1>
          <p className="text-sm text-muted-foreground font-gc-reg mt-1">
            Define rules to tag your data at the request or session level. Use
            tags for dataset filtering and evaluation slicing.
          </p>
        </div>
        <Button onClick={handleNew} className="gap-2">
          <PlusIcon className="w-4 h-4" />
          New Tag
        </Button>
      </div>

      {/* Base Tags */}
      <TagSection
        title="Base Tags"
        subtitle="Simple rules that detect atomic patterns"
        icon={<TagIcon className="w-3.5 h-3.5" />}
        tags={baseTags}
        loading={loading}
        deleting={deleting}
        onEdit={handleEdit}
        onDelete={handleDelete}
        onRun={handleRun}
        emptyText='No base tags yet. Click "New Tag" to create one.'
      />

      {/* Derived Tags */}
      {derivedTags.length > 0 && (
        <TagSection
          title="Derived Tags"
          subtitle="Composite and sequence rules built from base tags"
          icon={<LayersIcon className="w-3.5 h-3.5" />}
          tags={derivedTags}
          loading={loading}
          deleting={deleting}
          onEdit={handleEdit}
          onDelete={handleDelete}
          onRun={handleRun}
          emptyText="No derived tags."
        />
      )}

      <TagDefinitionModal
        open={modalOpen}
        onClose={() => {
          setModalOpen(false);
          setEditingTag(null);
        }}
        onSave={handleModalSave}
        tag={editingTag}
        existingTags={tags}
      />

      <TagRunDrawer
        open={runDrawerOpen}
        onClose={() => {
          setRunDrawerOpen(false);
          setRunningTag(null);
        }}
        tag={runningTag}
      />
    </div>
  );
};

export default TagsPage;

/* ---------- TagSection ---------- */

interface TagSectionProps {
  title: string;
  subtitle: string;
  icon: React.ReactNode;
  tags: TagDefinition[];
  loading: boolean;
  deleting: number | null;
  onEdit: (t: TagDefinition) => void;
  onDelete: (t: TagDefinition) => void;
  onRun: (t: TagDefinition) => void;
  emptyText: string;
}

const TagSection: React.FC<TagSectionProps> = ({
  title,
  subtitle,
  icon,
  tags,
  loading,
  deleting,
  onEdit,
  onDelete,
  onRun,
  emptyText,
}) => (
  <div className="space-y-3">
    <div>
      <h2 className="text-sm font-gc-med text-muted-foreground uppercase tracking-wider flex items-center gap-2">
        {icon}
        {title}
      </h2>
      <p className="text-xs text-muted-foreground mt-0.5">{subtitle}</p>
    </div>
    <Card className="border-border">
      <CardContent className="p-0">
        {loading && tags.length === 0 ? (
          <div className="flex items-center justify-center py-12">
            <Loader2Icon className="w-5 h-5 animate-spin text-muted-foreground" />
          </div>
        ) : tags.length === 0 ? (
          <div className="text-center py-10 px-4">
            <p className="text-sm text-muted-foreground font-gc-reg">
              {emptyText}
            </p>
          </div>
        ) : (
          <div className="divide-y divide-border">
            {tags.map((t) => (
              <TagRow
                key={t.id}
                tag={t}
                deleting={deleting}
                onEdit={onEdit}
                onDelete={onDelete}
                onRun={onRun}
              />
            ))}
          </div>
        )}
      </CardContent>
    </Card>
  </div>
);

/* ---------- TagRow ---------- */

interface TagRowProps {
  tag: TagDefinition;
  deleting: number | null;
  onEdit: (t: TagDefinition) => void;
  onDelete: (t: TagDefinition) => void;
  onRun: (t: TagDefinition) => void;
}

const TagRow: React.FC<TagRowProps> = ({
  tag,
  deleting,
  onEdit,
  onDelete,
  onRun,
}) => (
  <div className="flex items-center justify-between px-4 py-3 hover:bg-accent/50 transition-colors">
    <div className="flex items-center gap-3 min-w-0">
      <div className="flex items-center justify-center w-8 h-8 rounded-md bg-accent text-muted-foreground">
        {RULE_TYPE_ICONS[tag.rule_type] || <TagIcon className="w-4 h-4" />}
      </div>
      <div className="min-w-0">
        <div className="flex items-center gap-2">
          <span className="font-gc-med text-sm text-foreground truncate">
            {tag.name}
          </span>
          <Badge variant="outline" className="text-[10px] shrink-0">
            {SCOPE_LABELS[tag.scope] || tag.scope}
          </Badge>
          <Badge variant="secondary" className="text-[10px] shrink-0">
            {RULE_TYPE_LABELS[tag.rule_type] || tag.rule_type}
          </Badge>
        </div>
        {tag.description && (
          <p className="text-xs text-muted-foreground truncate max-w-[400px]">
            {tag.description}
          </p>
        )}
      </div>
    </div>
    <div className="flex items-center gap-1 shrink-0">
      <Button
        variant="ghost"
        size="icon"
        onClick={() => onRun(tag)}
        title="Run tag"
      >
        <PlayIcon className="w-4 h-4" />
      </Button>
      <Button
        variant="ghost"
        size="icon"
        onClick={() => onEdit(tag)}
        title="Edit"
      >
        <PencilIcon className="w-4 h-4" />
      </Button>
      <Button
        variant="ghost"
        size="icon"
        onClick={() => onDelete(tag)}
        disabled={deleting === tag.id}
        title="Delete"
      >
        {deleting === tag.id ? (
          <Loader2Icon className="w-4 h-4 animate-spin" />
        ) : (
          <Trash2Icon className="w-4 h-4" />
        )}
      </Button>
    </div>
  </div>
);
